// Trazado por calles de cada recorrido del chofer en foco (Valhalla, vía
// servidor); si no se puede, línea recta punteada.
//
//  · Cada trazado queda atado al chofer para el que se pidió: al pasar a otro
//    no se dibuja el del anterior con las paradas del nuevo mientras llega.
//  · Se guarda por puntos (el refresco de las capas no lo vuelve a pedir si
//    nada se movió), pero solo lo que salió bien: una falla pasajera de
//    Valhalla no deja la línea recta para siempre; se reintenta en el
//    próximo refresco (igual que el armado de recorridos).
import { useEffect, useRef, useState } from "react";
import type { MapStop } from "../lib/mapLayers";
import type { Trazado } from "../lib/mapDraw";
import type { LatLng } from "../lib/leafletTypes";
import { requestJson } from "../lib/requestJson";
import { decodePolyline } from "../lib/streetRouting";

export function useFocoTrazados(foco: number | null, paradas: MapStop[], token: string | null | undefined): Record<number, Trazado> {
  const [trazados, setTrazados] = useState<{ foco: number; porRecorrido: Record<number, Trazado> } | null>(null);
  const cache = useRef<Map<string, Trazado>>(new Map());

  useEffect(() => {
    if (foco == null) return;
    let vivo = true;
    const porRecorrido = new Map<number, LatLng[]>();
    for (const s of paradas) {
      if (s.latitude == null || s.longitude == null) continue;
      porRecorrido.set(s.route_id, [...(porRecorrido.get(s.route_id) ?? []), [s.latitude, s.longitude]]);
    }
    (async () => {
      const out: Record<number, Trazado> = {};
      for (const [routeId, pts] of Array.from(porRecorrido)) {
        if (pts.length < 2) continue;
        const clave = JSON.stringify(pts);
        let t = cache.current.get(clave);
        if (!t) {
          try {
            const json = await requestJson("/v2/routes/street/shape", { token, method: "POST", body: { points: pts.map(([lat, lng]) => ({ lat, lng })) } });
            if (!json?.ok) throw new Error(json?.error || "sin trazado");
            t = { lines: (json.shapes as string[]).map((sh) => decodePolyline(sh)), straight: false, km: json.km ?? null };
            cache.current.set(clave, t);
          } catch {
            t = { lines: [pts], straight: true, km: null };
          }
        }
        out[routeId] = t;
      }
      if (vivo) setTrazados({ foco, porRecorrido: out });
    })();
    return () => { vivo = false; };
  }, [foco, paradas, token]);

  return foco != null && trazados?.foco === foco ? trazados.porRecorrido : SIN_TRAZADOS;
}

const SIN_TRAZADOS: Record<number, Trazado> = {};
